import { FC, LazyExoticComponent, MouseEventHandler, Suspense } from 'react';
import { CircularProgress, SvgIconProps } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import { CardProps } from '@mui/material/Card/Card';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

export const CARD_HEIGHT = 100;
interface Props {
  value?: string | number;
  cardTitle?: string;
  onClick?: MouseEventHandler;
  selected?: boolean;
  props?: CardProps;
  CardsIcon?: LazyExoticComponent<(props: SvgIconProps) => JSX.Element>;
  cardColor?: string;
}

const DashboardCard: FC<Props> = ({
  value = '',
  cardTitle,
  children,
  onClick,
  selected,
  props,
  CardsIcon,
  cardColor,
}) => {
  return (
    <StyledCard
      {...props}
      onClick={onClick}
      cardColor={cardColor}
      sx={{
        ...(selected && {
          ...selectedCardStyle,
        }),
      }}>
      <StyledContentWrapper cardColor={cardColor}>
        <div>
          <Item>
            {CardsIcon && (
              <Suspense fallback={<CircularProgress />}>
                <CardsIcon />
              </Suspense>
            )}
          </Item>
        </div>
        <div>
          {cardTitle && <StyledTitle>{cardTitle}</StyledTitle>}
          <StyledCount>{value}</StyledCount>
        </div>
      </StyledContentWrapper>
      {children}
    </StyledCard>
  );
};

export default DashboardCard;

const Item = styled(Box)(({ theme }) => ({
  order: 2,
  '& svg': {
    fontSize: 40,
  },
  color: theme.palette.text.secondary,
}));

const selectedCardStyle = {
  cursor: 'pointer',
  border: '2px solid #047DDB80',
  borderRadius: '5px',
  backgroundColor: '#e3f2fd0f',
};
const StyledCard = styled(Card)<{ cardColor?: string }>(
  ({ theme, onClick, cardColor }) => ({
    background: `linear-gradient(90deg, ${cardColor} -200%, rgba(255, 255, 255, 1) 60%)`,
    [theme.breakpoints.up('sm')]: {
      height: 'auto',
    },
    [theme.breakpoints.up('md')]: {
      height: CARD_HEIGHT / 1.2,
    },
    [theme.breakpoints.up('lg')]: {
      height: CARD_HEIGHT,
    },
    [theme.breakpoints.up('xl')]: {
      height: CARD_HEIGHT,
    },
    height: CARD_HEIGHT,
    fontSize: 16,
    '&:hover': onClick
      ? {
          ...selectedCardStyle,
        }
      : undefined,
  }),
);
const StyledTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontFamily: 'Arial',
  padding: 0,
  margin: 0,
}));
const StyledCount = styled(Typography)(({ theme }) => ({
  fontFamily: 'Arial',
  fontWeight: 'bold',
  padding: 0,
  margin: 0,
  [theme.breakpoints.down('md')]: {
    fontSize: 18,
  },
  [theme.breakpoints.up('md')]: {
    fontSize: 24,
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: 28,
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: 36,
  },
}));

const StyledContentWrapper = styled(Box)<{ cardColor?: string }>(
  ({ theme, cardColor }) => ({
    paddingLeft: theme.spacing(1),
    height: '100%',
    borderLeft: `10px solid ${cardColor}`,
    display: 'grid',
    gridTemplateColumns: '40px 1fr',
    gap: theme.spacing(1),
    alignItems: 'center',
  }),
);
